import logoPlus_Light from "~/assets/logos/logo-plus-horizontal-light.svg";
import logoPlus_Dark from "~/assets/logos/logo-plus-horizontal-dark.svg";
import logoIndex_Light from "~/assets/logos/logo-index-horizontal-light.svg";
import logoIndex_Dark from "~/assets/logos/logo-index-horizontal-dark.svg";
import logoNitro_Light from "~/assets/logos/logo-nitro-horizontal-light.svg";
import logoNitro_Dark from "~/assets/logos/logo-nitro-horizontal-dark.svg";
import logoEngine_Light from "~/assets/logos/logo-engine-horizontal-light.svg";
import logoEngine_Dark from "~/assets/logos/logo-engine-horizontal-dark.svg";
import logoRisingStars_Light from "~/assets/logos/logo-rising-stars-horizontal-light.svg";
import logoRisingStars_Dark from "~/assets/logos/logo-rising-stars-horizontal-dark.svg";
import logoAkl_Light from "~/assets/logos/logo-akl-horizontal-light.svg";
import logoAkl_Dark from "~/assets/logos/logo-akl-horizontal-dark.svg";
import logoLigagg_Light from "~/assets/logos/logo-ligagg-horizontal-light.svg";
import logoLigagg_Dark from "~/assets/logos/logo-ligagg-horizontal-dark.svg";

export interface HeroSection {
  type: "hero";
}

export interface OverviewSection {
  type: "overview";
  description: string;
  stats?: { label: string; value: string }[];
  externalUrl?: string;
}

export interface ColorPaletteSection {
  type: "colors";
  primary: { name: string; hex: string };
  secondary?: { name: string; hex: string }[];
  usage: string;
}

export interface LogoVariantsSection {
  type: "logos";
  variants: {
    label: string;
    lightSrc: string;
    darkSrc: string;
  }[];
  clearSpace: string;
  donts?: string[];
}

export interface TypographySection {
  type: "typography";
  primary: { name: string; weights: string[]; sample: string };
  secondary?: { name: string; weights: string[]; sample: string };
}

export interface GuidelinesSection {
  type: "guidelines";
  title: string;
  items: { heading: string; body: string }[];
}

export interface StatusSection {
  type: "status";
  phase: "active" | "beta" | "coming-soon";
  launchDate?: string;
  roadmapItems?: string[];
}

export type ProductSection =
  | HeroSection
  | OverviewSection
  | ColorPaletteSection
  | LogoVariantsSection
  | TypographySection
  | GuidelinesSection
  | StatusSection;

export interface ProductSigil {
  id: string;
  name: string;
  shortName: string;
  slug: string;
  tagline: string;
  accentColor: string;
  comingSoon?: boolean;
  status: "active" | "beta" | "coming-soon";
  externalUrl?: string;
  sections: ProductSection[];
  // Dynamic brand switching fields
  horizontalLogoLight?: string;  // Vite-resolved SVG URL for light theme
  horizontalLogoDark?: string;   // Vite-resolved SVG URL for dark theme
  namePrefix?: string;           // e.g. "Gamin" — rendered in prefixColor
  nameSuffix?: string;           // e.g. "PLUS" — rendered in accentColor
  prefixColor?: string;          // hex for prefix text (defaults to theme-aware neutral)
}

export const productSigils: ProductSigil[] = [
  {
    id: "exp",
    name: "EngageXP",
    shortName: "EXP",
    slug: "exp",
    tagline: "LATAM's Gaming Conversion Partner",
    accentColor: "#A335EE",
    externalUrl: "https://engagexp.gg",
    status: "active",
    namePrefix: "Engage",
    nameSuffix: "XP",
    // Suffix uses accentColor; prefix is theme-aware in ProductHero
    sections: [
      { type: "hero" },
      {
        type: "colors",
        primary: { name: "Epic Purple", hex: "#A335EE" },
        secondary: [
          { name: "Rare Blue", hex: "#136AF4" },
          { name: "Legendary Orange", hex: "#FF7800" },
          { name: "Digital Black", hex: "#0F0F0F" },
        ],
        usage:
          "Parent EngageXP brand: Epic Purple is the identity anchor. Rare Blue and Legendary Orange carry MAP / CONVERT semantics in funnels. Open Color System for full neutral and scale reference.",
      },
    ],
  },
  {
    id: "nitro",
    name: "Gamin NITRO",
    shortName: "NITRO",
    slug: "nitro",
    tagline: "Self-service SMB gaming marketing",
    accentColor: "#FF7800",
    comingSoon: true,
    status: "coming-soon",
    horizontalLogoLight: logoNitro_Light,
    horizontalLogoDark: logoNitro_Dark,
    namePrefix: "Gamin",
    nameSuffix: "NITRO",
    sections: [
      { type: "hero" },
      {
        type: "colors",
        primary: { name: "NITRO Orange", hex: "#FF7800" },
        secondary: [
          { name: "Charcoal", hex: "#1A1A1A" },
          { name: "White", hex: "#FFFFFF" },
        ],
        usage:
          "Legendary orange is the hero accent for SMB self-serve energy. Use on dark surfaces; avoid orange-on-orange. Parent Epic Purple still applies to EngageXP chrome where co-branded.",
      },
    ],
  },
  {
    id: "plus",
    name: "Gamin PLUS",
    shortName: "PLUS",
    slug: "plus",
    tagline: "Carrier-billed gaming subscription",
    accentColor: "#D6F33F",
    externalUrl: "https://gaminplus.com.br",
    status: "active",
    horizontalLogoLight: logoPlus_Light,
    horizontalLogoDark: logoPlus_Dark,
    namePrefix: "Gamin",
    nameSuffix: "PLUS",
    sections: [
      { type: "hero" },
      {
        type: "overview",
        description: "Gamin PLUS is a carrier-billed gaming subscription platform in partnership with Claro Brasil. Subscribers get access to curated gaming content, exclusive tournaments, and digital rewards — all charged directly to their phone bill.",
        stats: [
          { label: "Active subscribers", value: "389K+" },
          { label: "Gross revenue", value: "R$50M+" },
          { label: "Partner", value: "Claro Brasil" },
        ],
        externalUrl: "https://gaminplus.com.br",
      },
      {
        type: "colors",
        primary: { name: "PLUS Green", hex: "#D6F33F" },
        secondary: [
          { name: "PLUS Dark", hex: "#1A1A1A" },
          { name: "PLUS White", hex: "#FFFFFF" },
        ],
        usage: "Green is the primary accent. Always use on dark backgrounds for maximum contrast. Never use green on light backgrounds without a dark container.",
      },
      {
        type: "logos",
        variants: [
          { label: "Icon", lightSrc: "sigil-plus-black.svg", darkSrc: "sigil-plus.svg" },
          { label: "Horizontal", lightSrc: "plus-horizontal-black.svg", darkSrc: "plus-horizontal-white.svg" },
        ],
        clearSpace: "Minimum 0.5x icon height on all sides",
      },
      {
        type: "guidelines",
        title: "Usage Rules",
        items: [
          { heading: "Co-branding with Claro", body: "Always place the Claro logo at equal or larger size when co-branding. PLUS logo goes left, Claro goes right." },
          { heading: "Tournament assets", body: "Use the PLUS Icon variant (not full logo) inside tournament brackets and scoreboards." },
        ],
      },
    ],
  },
  {
    id: "index",
    name: "Gamin INDEX",
    shortName: "INDEX",
    slug: "index",
    tagline: "Gaming market intelligence",
    accentColor: "#6366F1",
    status: "beta",
    horizontalLogoLight: logoIndex_Light,
    horizontalLogoDark: logoIndex_Dark,
    namePrefix: "Gamin",
    nameSuffix: "INDEX",
    sections: [
      { type: "hero" },
      {
        type: "colors",
        primary: { name: "INDEX Indigo", hex: "#6366F1" },
        secondary: [
          { name: "Data Ink", hex: "#312E81" },
          { name: "White", hex: "#FFFFFF" },
        ],
        usage:
          "Indigo is the intelligence and data accent—use for INDEX dashboards, charts, and hero moments. Pair with deep navy surfaces; reserve EngageXP purple for corporate / parent CTAs when both brands appear.",
      },
    ],
  },
  {
    id: "rising-stars",
    name: "Rising Stars Clash",
    shortName: "RSC",
    slug: "rising-stars",
    tagline: "Emerging talent competitive program",
    accentColor: "#FFC20E",
    status: "active",
    horizontalLogoLight: logoRisingStars_Light,
    horizontalLogoDark: logoRisingStars_Dark,
    sections: [
      { type: "hero" },
      {
        type: "colors",
        primary: { name: "Rising Gold", hex: "#FFC20E" },
        secondary: [
          { name: "Deep Black", hex: "#0A0A0A" },
          { name: "White", hex: "#FFFFFF" },
        ],
        usage:
          "Solar gold is the competitive spotlight color—trophies, highlights, and finals energy. Keep text on gold in black or very dark blue for contrast.",
      },
    ],
  },
  {
    id: "akl",
    name: "AKL",
    shortName: "AKL",
    slug: "akl",
    tagline: "Premium esports tournament circuit",
    accentColor: "#2563EB",
    status: "active",
    horizontalLogoLight: logoAkl_Light,
    horizontalLogoDark: logoAkl_Dark,
    sections: [
      { type: "hero" },
      {
        type: "colors",
        primary: { name: "AKL Finals Blue", hex: "#2563EB" },
        secondary: [
          { name: "Arena Black", hex: "#020617" },
          { name: "White", hex: "#FFFFFF" },
        ],
        usage:
          "Electric blue signals premium circuit identity—broadcast overlays, brackets, and stage graphics. Do not substitute EngageXP Epic Purple for AKL campaign art.",
      },
    ],
  },
  {
    id: "ligagg",
    name: "LigaGG",
    shortName: "LGG",
    slug: "ligagg",
    tagline: "Community tournament platform",
    accentColor: "#DC2626",
    externalUrl: "https://ligagg.com",
    status: "active",
    horizontalLogoLight: logoLigagg_Light,
    horizontalLogoDark: logoLigagg_Dark,
    sections: [
      { type: "hero" },
      {
        type: "colors",
        primary: { name: "LigaGG Crimson", hex: "#DC2626" },
        secondary: [
          { name: "Pitch Black", hex: "#18181B" },
          { name: "White", hex: "#FFFFFF" },
        ],
        usage:
          "Crimson is the community competitive red—use for LigaGG-owned surfaces and tournament UI. Balance with neutrals; avoid mixing with AKL blue in the same lockup.",
      },
    ],
  },
  {
    id: "engine",
    name: "Gamin ENGINE",
    shortName: "ENGINE",
    slug: "engine",
    tagline: "Tournament & competition management",
    accentColor: "#22C55E",
    status: "active",
    horizontalLogoLight: logoEngine_Light,
    horizontalLogoDark: logoEngine_Dark,
    namePrefix: "Gamin",
    nameSuffix: "ENGINE",
    sections: [
      { type: "hero" },
      {
        type: "colors",
        primary: { name: "ENGINE Volt Green", hex: "#22C55E" },
        secondary: [
          { name: "Tournament Black", hex: "#0C0C0F" },
          { name: "White", hex: "#FFFFFF" },
        ],
        usage:
          "Volt green signals live ops and brackets—pair with near-black backgrounds. Distinct from PLUS lime (#D6F33F); do not interchange in ENGINE marketing.",
      },
    ],
  },
];
